/* eslint-disable @next/next/no-img-element */
"use client";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useGetCategoryListQuery, useGetCategoryDashboardQuery } from "@/service/home";
import { CategoryItem } from "@/types/General";

function HomeCategorySlider() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentTab = searchParams.get("tab") || "for-you";
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const sliderRef = useRef<any>(null);

  const { data: categoryData } = useGetCategoryListQuery();
  const { data: dashboardData, isLoading: isDashboardLoading } = useGetCategoryDashboardQuery(
    { categoryId: selectedCategoryId as number },
    {
      skip: !selectedCategoryId || selectedCategoryId === -1,
    }
  );

  const [categories, setCategories] = useState<
    { id: number; name: string; slug: string; icon: string | null }[]
  >([
    { id: -1, name: "For You", slug: "for-you", icon: "/images/cat_icon1.svg" },
  ]);

  const settings = {
    infinite: false, // Disable infinite to prevent showing items before "For You"
    speed: 300,
    slidesToShow: 7,
    slidesToScroll: 1,
    arrows: false,
    dots: false,
    cssEase: "ease-in-out",
    swipe: true,
    draggable: true,
    touchMove: true,
    swipeToSlide: true,
    touchThreshold: 5,
    initialSlide: 0,
    variableWidth: true,
    centerMode: false,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 1,
          arrows: false,
          infinite: false,
          variableWidth: true,
          swipe: true,
          draggable: true,
          touchMove: true,
          swipeToSlide: true,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
          arrows: false,
          infinite: false,
          variableWidth: true,
          swipe: true,
          draggable: true,
          touchMove: true,
          swipeToSlide: true,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          arrows: false,
          infinite: false,
          variableWidth: true,
          swipe: true,
          draggable: true,
          touchMove: true,
          swipeToSlide: true,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: false,
          infinite: false,
          variableWidth: true,
          swipe: true,
          draggable: true,
          touchMove: true,
          swipeToSlide: true,
        },
      },
    ],
  };

  useEffect(() => {
    if (categoryData?.statusCode === 200 && categoryData.data) {
      const dynamicCategories = categoryData.data.map((cat: CategoryItem) => ({
        id: cat.id,
        name: cat.name,
        slug: cat.name.toLowerCase().replace(/ & /g, "-").replace(/ /g, "-"),
        icon: cat.icon_url,
      }));
      setCategories([
        { id: -1, name: "For You", slug: "for-you", icon: "/images/cat_icon1.svg" },
        ...dynamicCategories,
      ]);
      // Reset slider to first slide (For You) when categories are loaded
      setTimeout(() => {
        if (sliderRef.current) {
          sliderRef.current.slickGoTo(0);
        }
      }, 100);
    }
  }, [categoryData]);

  // Reset slider to first slide when "For You" is selected
  useEffect(() => {
    if (currentTab === "for-you" && sliderRef.current) {
      setTimeout(() => {
        sliderRef.current?.slickGoTo(0);
      }, 50);
    }
  }, [currentTab]);

  const handleSelect = (categorySlug: string, categoryId: number) => {
    if (categorySlug === "for-you") {
      router.push("/");
      setSelectedCategoryId(null);
    } else {
      router.push(`/?tab=${categoryId}`);
      setSelectedCategoryId(categoryId);
    }
  };

  return (
    <section className="category_slider_sc">
      <div className="container" style={{   marginLeft: '180px'}}>
        <Slider ref={sliderRef} {...settings} className="category_slider_row" >
          {categories.map((category) => (
            <div key={category.id}>
              <div
                className={`category_slide_item ${
                  currentTab === category.id.toString() ||
                  (currentTab === "for-you" && category.id === -1)
                    ? "active"
                    : ""
                }`}              onClick={() => handleSelect(category.slug, category.id)}
                style={{ padding: '15px', height: '100%' }}
              >
                <figure style={{width: '36px', height: '36px'}}>
                  <img src={category.icon || "/images/cat_icon1.svg"} alt={category.name} onError={(e) => {
                    (e.target as HTMLImageElement).src = "/images/cat_icon1.svg";
                  }} />
                </figure>
                <h3
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color:
                      (currentTab === category.id.toString() ||
                        (currentTab === "for-you" && category.id === -1))
                        ? '#110312'
                        : '#666',
                    margin: '0',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.2',
                    width: 'fit-content',
                  }}
                >
                  {category.name}
                </h3>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </section>
  );
}

export default HomeCategorySlider;
